import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Sparkles,
  Car,
  HeartPulse,
  Home,
  Shield,
  Loader2,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quickActions = {
  particulier: [
    { label: "Devis Auto", icon: Car, action: "auto" },
    { label: "Devis Santé", icon: HeartPulse, action: "sante" },
    { label: "Devis Habitation", icon: Home, action: "multirisque" },
  ],
  professionnel: [
    { label: "RC Décennale", icon: Shield, action: "rc_decennale" },
    { label: "Cyber-sécurité", icon: Shield, action: "cybersecurite" },
    { label: "Multirisque Pro", icon: Home, action: "multirisque_pro" },
  ],
};

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

export default function ChatBot({ isOpen, onClose, userType = "particulier" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      const welcomeMessage = userType === "professionnel"
        ? "Bonjour ! Je suis votre conseiller JYVIS Assurances. Comment puis-je vous aider avec vos assurances professionnelles ? RC Décennale, multirisque, cyber-sécurité..."
        : "Bonjour ! Je suis votre conseiller JYVIS Assurances. Assurer son quotidien, c'est assurer son avenir ! Comment puis-je vous aider ?";
      
      setMessages([
        {
          role: "assistant",
          content: welcomeMessage,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, userType]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
        user_type: userType,
      });

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error("Chat error:", err);
      const errorMessage = {
        role: "assistant",
        content: "Désolé, je rencontre un problème technique. Veuillez réessayer ou contacter notre équipe au 01 23 45 67 89.",
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    }

    setLoading(false);
  };

  const handleQuickAction = (action) => {
    const prompts = {
      auto: "Je souhaite obtenir un devis pour une assurance auto",
      sante: "Je cherche une complémentaire santé adaptée à mes besoins",
      multirisque: "J'aimerais assurer mon logement",
      rc_decennale: "Je suis professionnel du bâtiment et j'ai besoin d'une RC Décennale",
      cybersecurite: "Je souhaite protéger mon entreprise contre les cyber-risques",
      multirisque_pro: "J'ai besoin d'assurer mes locaux professionnels",
    };
    sendMessage(prompts[action] || `Je m'intéresse à ${action}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 md:bg-transparent md:backdrop-blur-none md:pointer-events-none"
            onClick={onClose}
          />

          {/* Chat Window */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-4 right-4 z-50 w-full max-w-md md:w-96"
            data-testid="chatbot-window"
          >
            <Card className="shadow-2xl border-0 overflow-hidden">
              {/* Header - Blue */}
              <div className="bg-[#102692] text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={JYVIS_LOGO} alt="JYVIS" className="h-8 w-auto rounded" />
                  <div>
                    <h3 className="font-semibold text-sm">Conseiller JYVIS</h3>
                    <p className="text-xs text-white/70 flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      En ligne
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={onClose}
                  data-testid="close-chatbot"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Messages */}
              <ScrollArea className="h-80 p-4" ref={scrollRef}>
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-2xl ${
                          message.role === "user"
                            ? "bg-primary text-white rounded-br-md"
                            : message.isError
                            ? "bg-red-50 text-red-800 rounded-bl-md"
                            : "bg-slate-100 text-slate-800 rounded-bl-md"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {message.role === "assistant" && (
                            <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                          )}
                          <p className="text-sm leading-relaxed">{message.content}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {loading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-slate-100 text-slate-800 p-3 rounded-2xl rounded-bl-md">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">En train d'écrire...</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </ScrollArea>

              {/* Quick Actions */}
              {messages.length <= 1 && (
                <div className="px-4 pb-2">
                  <p className="text-xs text-slate-500 mb-2">Actions rapides :</p>
                  <div className="flex flex-wrap gap-2">
                    {quickActions[userType]?.map((action, i) => {
                      const Icon = action.icon;
                      return (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => handleQuickAction(action.action)}
                          data-testid={`quick-action-${action.action}`}
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {action.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t bg-white">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Posez votre question..."
                    className="flex-1"
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={() => sendMessage(input)}
                    disabled={!input.trim() || loading}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Propulsé par l'IA • Réponses en temps réel
                </p>
              </div>
            </Card>
          </motion.div>
        </>
      )}

      {/* Floating Button (when closed) */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {}} // Will be controlled by parent
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 w-14 h-14 bg-accent text-white rounded-full shadow-lg flex items-center justify-center hover:bg-accent/90 transition-colors"
          data-testid="floating-chat-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
